import React, { useCallback, useEffect, useState } from 'react';
import { Button, Divider, List, Popover, Spin, Tag, Typography } from 'antd';
import { CalendarOutlined, ClockCircleOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { history } from '@umijs/max';
import dayjs from 'dayjs';
import {
  AutoScheduleFailure,
  getAutoScheduleFailures,
  onAutoScheduleFailuresUpdated,
} from '@/services/app/tenant/automations';
import { ensureNotificationSocketConnected } from '@/services/app/websocket';
import { useJitteredReload } from '@/hooks/useJitteredReload';
import styles from './index.less';

/** Safety-net re-fetch for a push that never arrived (visible tabs only). */
const SAFETY_POLL_MS = 5 * 60 * 1000;

/**
 * Nav-bar badge for jobs the auto-schedule automation could NOT book on their
 * requested date (client decision: never overbook, never move the date —
 * flag it and tell the office instead). Visible to ALL tenant users.
 *
 * State-driven, not notification-driven: the list is the set of flagged jobs
 * that are STILL unscheduled, so it can't be "marked read" away while the
 * work is undone and it clears itself the moment someone books the job.
 * Live-refreshed by its own WS push (AUTO_SCHEDULE_FAILURES_UPDATED, sent only
 * when a flag or a flagged job's appointment changes), not by every
 * notifications-updated push.
 * Hidden entirely while there is nothing to schedule.
 */
const AutoScheduleBadge: React.FC = () => {
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems]     = useState<AutoScheduleFailure[]>([]);
  const [count, setCount]     = useState(0);

  const fetchFailures = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const data = await getAutoScheduleFailures();
      setItems(data?.items ?? []);
      setCount(data?.count ?? 0);
    } catch {
      // Silent — the badge is informational; the next refresh retries.
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  // Every open tab of the tenant gets the push at once — spread the re-fetches
  // and fold a burst into one (the reconnect resync comes through here too).
  const refetchFromPush = useJitteredReload(fetchFailures);

  // Initial load + live refresh on the badge's own WS push.
  useEffect(() => {
    fetchFailures();
    ensureNotificationSocketConnected();
    return onAutoScheduleFailuresUpdated(refetchFromPush);
  }, [fetchFailures, refetchFromPush]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') fetchFailures();
    }, SAFETY_POLL_MS);
    return () => clearInterval(timer);
  }, [fetchFailures]);

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (next) fetchFailures(true);
  }, [fetchFailures]);

  const openCustomer = (item: AutoScheduleFailure) => {
    setOpen(false);
    if (item.customer_id) {
      // dispatch_id deep link auto-opens the job's detail drawer on arrival.
      history.push(`/tenant/sales-crm/customer/detail/${item.customer_id}?dispatch_id=${item.dispatch_id}`);
    }
  };

  // Nothing pending — keep the topbar clean.
  if (count === 0) return null;

  const content = (
    <div style={{ width: 400 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Typography.Text strong>Auto-Schedule Failed — Needs Manual Scheduling</Typography.Text>
        <Tag color="orange">{count}</Tag>
      </div>

      <Spin spinning={loading}>
        <List
          dataSource={items}
          style={{ maxHeight: 420, overflowY: 'auto' }}
          renderItem={(item) => (
            <List.Item style={{ padding: '10px 4px', display: 'block', borderBottom: '1px solid #f0f0f0' }}>
              <Typography.Text strong style={{ fontSize: 13 }}>
                {item.customer_name || `Job #${item.dispatch_id}`}
              </Typography.Text>

              {item.requested_date && (
                <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>
                  <CalendarOutlined style={{ marginRight: 4 }} />
                  Requested date: {dayjs(item.requested_date).format('MM/DD/YYYY')}
                </div>
              )}

              {item.reason && (
                <div style={{ fontSize: 12, color: '#d46b08', marginTop: 2 }}>{item.reason}</div>
              )}

              <div style={{ fontSize: 11, color: '#999', margin: '4px 0' }}>
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                Created {item.created_at ? dayjs(item.created_at).format('MMM D, h:mm A') : ''}
                <span style={{ margin: '0 6px' }}>·</span>
                Job #{item.dispatch_id}
              </div>

              {item.customer_id && (
                <Button type="primary" size="small" style={{ marginTop: 4 }} onClick={() => openCustomer(item)}>
                  Open Customer
                </Button>
              )}
            </List.Item>
          )}
        />
      </Spin>

      <Divider style={{ margin: '8px 0' }} />
      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
        These jobs could not be booked on their requested date — schedule them manually. They disappear
        from this list as soon as an appointment is set.
      </Typography.Text>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottomRight"
    >
      <span
        className={styles.action}
        style={{
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '3px 10px',
          borderRadius: 4,
          background: '#fff7e6',
          border: '1px solid #ffd591',
          color: '#d46b08',
          fontWeight: 500,
          fontSize: 13,
          lineHeight: '20px',
          whiteSpace: 'nowrap',
        }}
      >
        <ThunderboltOutlined />
        {count > 99 ? '99+' : count} to schedule
      </span>
    </Popover>
  );
};

export default AutoScheduleBadge;
